import React, { useState } from 'react';
import { 
  Bed, DollarSign, PieChart, Tool, Star, Users, ArrowUp, ArrowDown, 
  Trash, Settings, Home, PlusCircle, Clock, Maximize, CheckCircle, 
  AlertTriangle, Filter, BarChart, PenBox, Coins, X, Sparkles,
  Info, HelpCircle, RefreshCw, Search, ArrowUpDown, Calendar
} from 'lucide-react';

// Currency formatting utility
const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
};

const MAX_QUALITY = 5;
const BASE_UPGRADE_COST = 600; // Use the same name as in upgradeRoom
const QUALITY_COST_MULTIPLIER = 1.6; // Use the same name as

/**
 * Formats a number as a currency string (e.g., $1,234.56).
 * @param {number} value The number to format.
 * @param {string} currencySymbol The currency symbol (default: '$').
 * @param {number} decimalPlaces The number of decimal places (default: 0 for cleaner display).
 * @returns {string} The formatted currency string.
 */
const formatCurrency = (value, currencySymbol = '$', decimalPlaces = 0) => {
  // Ensure value is a number
  const numberValue = Number(value);
  if (isNaN(numberValue)) {
    return `${currencySymbol}0`; // Or handle error differently
  }

  // Use Intl.NumberFormat for robust formatting (handles locale-specific separators)
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD', // Use USD as a base, the symbol is overridden
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(numberValue).replace('USD', currencySymbol).trim(); // Replace code with symbol if needed
     // A simpler fallback if Intl isn't needed/available:
     // return `${currencySymbol}${numberValue.toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  } catch (error) {
      console.error("Error formatting currency:", error);
      // Fallback to a simpler format if Intl fails
      return `${currencySymbol}${numberValue.toFixed(decimalPlaces)}`;
  }
};

const RoomManagementTab = ({ 
  selectedTab, 
  roomTypes, 
  buildRoom, 
  money, 
  rooms, 
  renderStars, 
  adjustRoomPrice,
  setRoomTypePrice,
  upgradeRoom,
  // Sample values for stats
  occupancyRate = 62,
  avgRoomPrice = 89,
  avgRoomQuality = 3.2
}) => {
  // State for managing UI
  const [priceSetMode, setPriceSetMode] = useState(null);
  const [newTypePrice, setNewTypePrice] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [showRoomTips, setShowRoomTips] = useState(false);
  const [showPricingPanel, setShowPricingPanel] = useState(true);
  
  // Calculate room statistics
  const totalRooms = rooms.length;
  const occupiedRooms = rooms.filter(room => room.occupied).length;
  const vacantRooms = totalRooms - occupiedRooms;
  const occupancyPercentage = totalRooms > 0 ? (occupiedRooms / totalRooms) * 100 : 0;
  
  // Group rooms by type for summary
  const roomsByType = rooms.reduce((acc, room) => {
    acc[room.type] = (acc[room.type] || 0) + 1;
    return acc;
  }, {});

  // Filter rooms based on active filter
  const filteredRooms = rooms.filter(room => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'occupied') return room.occupied;
    if (activeFilter === 'vacant') return !room.occupied;
    if (activeFilter === 'upgrade') return room.quality < 5;
    if (Object.keys(roomsByType).includes(activeFilter)) return room.type === activeFilter;
    return true;
  }).filter(room => {
    if (!searchTerm) return true;
    return `Room ${room.id} ${room.type}`.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  // Sort rooms
  const sortedRooms = [...filteredRooms].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'id':
        comparison = a.id - b.id;
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'quality':
        comparison = a.quality - b.quality;
        break;
      case 'price':
        comparison = a.price - b.price;
        break;
      case 'status':
        comparison = a.occupied === b.occupied ? 0 : a.occupied ? 1 : -1;
        break;
      default:
        comparison = a.id - b.id;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Get room type details including icon
  const getRoomTypeDetails = (type) => {
    const typeInfo = {
      'Standard': { icon: <Bed className="w-4 h-4" />, color: 'blue', description: 'Basic accommodations' },
      'Deluxe': { icon: <Star className="w-4 h-4" />, color: 'purple', description: 'Premium features' },
      'Suite': { icon: <Maximize className="w-4 h-4" />, color: 'amber', description: 'Spacious luxury' },
      'PentHouse': { icon: <Home className="w-4 h-4" />, color: 'green', description: 'Ultimate experience' }
    };
    
    return typeInfo[type] || { icon: <Bed className="w-4 h-4" />, color: 'gray', description: 'Room accommodation' };
  };
  
  // Handle setting price for all rooms of a type
  const handleSetTypePrice = (type) => {
    if (priceSetMode === type) {
      // Confirm price setting
      // First, check if setRoomTypePrice function exists
      if (typeof setRoomTypePrice === 'function') {
        setRoomTypePrice(type, newTypePrice);
      } else {
        // Fallback: manually update prices for each room of this type
        rooms.forEach(room => {
          if (room.type === type) {
            adjustRoomPrice(room.id, newTypePrice);
          }
        });
      }
      setPriceSetMode(null);
      setNewTypePrice(0);
    } else {
      // Enter price setting mode
      setPriceSetMode(type);
      // Set initial value to the first room of this type's current price
      const firstRoomOfType = rooms.find(room => room.type === type);
      setNewTypePrice(firstRoomOfType ? firstRoomOfType.price : 0);
    }
  };

  const calculateUpgradeCost = (currentQuality) => {
    if (currentQuality >= MAX_QUALITY) {
      return Infinity; // Cannot upgrade further, cost is effectively infinite
    }
    // Use the exact same formula as in upgradeRoom
    return Math.floor(
      BASE_UPGRADE_COST * Math.pow(QUALITY_COST_MULTIPLIER, currentQuality)
    );
  };

  // Toggle sort order or change sort field
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Render filter badge
  const renderFilterBadge = (filterValue, label, icon) => {
    const isActive = activeFilter === filterValue;
    const Icon = icon;
    
    return (
      <button
        onClick={() => setActiveFilter(filterValue)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center transition-all
          ${isActive 
            ? 'bg-blue-600 text-white shadow-sm' 
            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
      >
        <Icon className="w-3.5 h-3.5 mr-1.5" />
        {label}
        {filterValue === 'all' && (
          <span className="ml-1.5 bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded-full text-xs">
            {rooms.length}
          </span>
        )}
        {filterValue === 'occupied' && (
          <span className="ml-1.5 bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded-full text-xs">
            {occupiedRooms}
          </span>
        )}
        {filterValue === 'vacant' && (
          <span className="ml-1.5 bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded-full text-xs">
            {vacantRooms}
          </span>
        )}
        {filterValue === 'upgrade' && (
          <span className="ml-1.5 bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded-full text-xs">
            {rooms.filter(room => room.quality < 5).length}
          </span>
        )}
        {Object.keys(roomsByType).includes(filterValue) && (
          <span className="ml-1.5 bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded-full text-xs">
            {roomsByType[filterValue]}
          </span>
        )}
      </button>
    );
  };

  // Handler for bulk price changes with percentage
  const [bulkPriceAdjustment, setBulkPriceAdjustment] = useState(0);
  const [selectedRoomType, setSelectedRoomType] = useState('all');
  
  const handleBulkPriceChange = () => {
    const targetRooms = selectedRoomType === 'all' 
      ? rooms 
      : rooms.filter(room => room.type === selectedRoomType);
      
    targetRooms.forEach(room => {
      const newPrice = Math.max(10, Math.round(room.price * (1 + bulkPriceAdjustment / 100)));
      adjustRoomPrice(room.id, newPrice);
    });
    
    // Reset after applying
    setBulkPriceAdjustment(0);
  };
  
  return (
    selectedTab === "rooms" && (
      <div className="bg-gray-50 rounded-xl shadow-sm p-2 sm:p-4 w-full overflow-hidden">
        {/* Modern Header with stats overview */}
        <div className="bg-white rounded-xl p-3 sm:p-4 mb-4 shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <div>
              <h2 className="text-xl font-bold flex items-center text-gray-800">
                <Bed className="mr-2 text-blue-600" />
                Room Management
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage your hotel's room inventory, pricing, and quality
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <div className="px-3 py-2 sm:px-4 sm:py-3 bg-blue-50 rounded-lg flex items-center border border-blue-200">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-600 bg-opacity-10 flex items-center justify-center mr-2 sm:mr-3">
                  <PieChart className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-xs text-blue-600 font-medium">Occupancy Rate</div>
                  <div className="text-base sm:text-lg font-bold text-blue-700">{occupancyRate.toFixed(1)}%</div>
                </div>
              </div>
              
              <div className="px-3 py-2 sm:px-4 sm:py-3 bg-green-50 rounded-lg flex items-center border border-green-200">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-600 bg-opacity-10 flex items-center justify-center mr-2 sm:mr-3">
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-xs text-green-600 font-medium">Average Price</div>
                  <div className="text-base sm:text-lg font-bold text-green-700">{formatMoney(avgRoomPrice)}</div>
                </div>
              </div>
              
              <div className="px-3 py-2 sm:px-4 sm:py-3 bg-amber-50 rounded-lg flex items-center border border-amber-200">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-600 bg-opacity-10 flex items-center justify-center mr-2 sm:mr-3">
                  <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-xs text-amber-600 font-medium">Quality Rating</div>
                  <div className="text-base sm:text-lg font-bold text-amber-700">{avgRoomQuality.toFixed(1)}/5</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Room Distribution Chart (simplified visual representation) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <PieChart className="w-4 h-4 mr-1 text-blue-500" />
                Room Distribution
              </h3>
              <div className="flex items-center space-x-2 mb-2">
                <div 
                  className="h-6 bg-blue-500 rounded-l-md" 
                  style={{ width: `${occupancyPercentage}%` }}
                ></div>
                <div 
                  className="h-6 bg-green-400 rounded-r-md" 
                  style={{ width: `${100 - occupancyPercentage}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-1"></div>
                  <span>Occupied: {occupiedRooms} rooms ({occupancyPercentage.toFixed(1)}%)</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-1"></div>
                  <span>Vacant: {vacantRooms} rooms ({(100 - occupancyPercentage).toFixed(1)}%)</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Home className="w-4 h-4 mr-1 text-blue-500" />
                Room Types
              </h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(roomsByType).map(([type, count]) => {
                  const details = getRoomTypeDetails(type);
                  const percentage = (count / totalRooms) * 100;
                  return (
                    <div 
                      key={type}
                      className="px-3 py-2 bg-white rounded-lg border border-gray-200 flex items-center"
                      style={{ minWidth: '120px' }}
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                        {details.icon}
                      </div>
                      <div>
                        <div className="text-xs font-medium">{type}</div>
                        <div className="text-xs text-gray-500">{count} rooms ({percentage.toFixed(0)}%)</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

          {/* Build Room Section */}
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
          <h3 className="font-semibold mb-3 flex items-center">
            <PlusCircle className="w-4 h-4 mr-2 text-blue-600" />
            Build New Rooms
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {roomTypes.map((roomType) => {
              const details = getRoomTypeDetails(roomType.type);
              const canAfford =(money + 5000) >= roomType.baseCost;
              
              return (
                <div 
                  key={roomType.type} 
                  className={`relative border rounded-lg p-3 overflow-hidden transition-all
                    ${canAfford 
                      ? `border-${details.color}-200 hover:border-${details.color}-300 bg-white hover:bg-${details.color}-50` 
                      : 'border-gray-200 bg-gray-50'}`}
                >
                  <div className="font-medium text-gray-800 mb-1 flex items-center">
                    {details.icon}
                    <span className="ml-1">{roomType.type}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm mb-3">
                    <div className="text-gray-500">Base cost:</div>
                    <div className="font-medium flex items-center">
                      <DollarSign className="w-3 h-3 mr-0.5 text-green-600" />
                      {roomType.baseCost}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => buildRoom(roomType.type)}
                    disabled={!canAfford}
                    className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors
                      ${canAfford
                        ? `bg-gradient-to-r from-${details.color}-600 to-${details.color}-500 text-white hover:from-${details.color}-500 hover:to-${details.color}-400 shadow-sm hover:shadow`
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    {canAfford ? (
                      <span className="flex items-center justify-center">
                        <PlusCircle className="w-4 h-4 mr-1" />
                        Build {roomType.type}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        Insufficient funds
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Interactive Room Type Pricing Panel */}
        {showPricingPanel && (
          <div className="bg-white rounded-xl p-3 sm:p-4 mb-4 shadow-sm border border-blue-100 relative">
            <button 
              onClick={() => setShowPricingPanel(false)}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
            
            <h3 className="font-semibold mb-4 flex items-center text-blue-700">
              <Coins className="w-5 h-5 mr-2" />
              Room Type Pricing Management
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.keys(roomsByType).map((type) => {
                const details = getRoomTypeDetails(type);
                const avgPriceForType = rooms
                  .filter(r => r.type === type)
                  .reduce((sum, r) => sum + r.price, 0) / roomsByType[type];
                  
                return (
                  <div 
                    key={type} 
                    className={`p-3 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition-all`}
                  >
                    <div className="flex justify-between md:flex-col items-center">
                      <div className="px-2 py-1 rounded-lg bg-blue-100 text-blue-600 font-medium text-sm flex items-center">
                        {details.icon}
                        <span className="ml-1">{type}</span>
                      </div>
                      <div className="text-blue-700 font-bold text-sm px-2 py-1 rounded-lg bg-blue-50">
                        {roomsByType[type]} rooms
                      </div>
                    </div>
                    
                    <div className="mt-3 mb-4">
                      <div className="text-xs text-gray-500">{details.description}</div>
                      <div className="mt-2 flex items-baseline">
                        <span className="text-2xl font-bold text-gray-800">
                          {formatMoney(avgPriceForType)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">/ night</span>
                      </div>
                    </div>
                    
                    {priceSetMode === type ? (
                      <div className="space-y-3">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                          </div>
                          <input 
                            type="number" 
                            value={newTypePrice}
                            onChange={(e) => setNewTypePrice(Math.max(0, parseInt(e.target.value) || 0))}
                            className="pl-7 w-full p-2 border-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-300 focus:outline-none"
                            placeholder="New price"
                            min="0"
                          />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSetTypePrice(type)}
                            className={`flex-1 px-3 py-2 bg-${details.color}-600 text-white rounded-lg text-sm hover:bg-${details.color}-700 transition-colors`}
                          >
                            <span className="flex items-center justify-center">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Apply
                            </span>
                          </button>
                          <button
                            onClick={() => setPriceSetMode(null)}
                            className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                          >
                            <span className="flex items-center justify-center">
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleSetTypePrice(type)}
                        className="w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors 
                          bg-blue-100 text-blue-700 border border-blue-200
                          hover:bg-blue-200"
                      >
                        <div className="flex items-center justify-center">
                          <Coins className="w-4 h-4 mr-1" />
                          Set Price for All {type} Rooms
                        </div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Bulk price adjustment feature */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-sm mb-3 text-gray-700">Bulk Price Adjustment</h4>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-grow max-w-xs">
                  <label className="block text-xs text-gray-500 mb-1">Select Room Type</label>
                  <select
                    value={selectedRoomType}
                    onChange={(e) => setSelectedRoomType(e.target.value)}
                    className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none"
                  >
                    <option value="all">All Room Types</option>
                    {Object.keys(roomsByType).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex-grow max-w-xs">
                  <label className="block text-xs text-gray-500 mb-1">Adjust Price By Percentage</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={bulkPriceAdjustment}
                      onChange={(e) => setBulkPriceAdjustment(parseFloat(e.target.value) || 0)}
                      className="w-full p-2 pr-8 border rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:outline-none"
                      step="1"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">%</span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleBulkPriceChange}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors font-medium"
                  disabled={bulkPriceAdjustment === 0}
                >
                  <span className="flex items-center">
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Apply Changes
                  </span>
                </button>
              </div>
              
              <div className="mt-2 text-xs text-gray-500">
                <Info className="w-3 h-3 inline-block mr-1" />
                Positive values increase prices, negative values decrease prices
              </div>
            </div>
          </div>
        )}
        
        {/* Room List with improved filters and search */}
        <div className="bg-white rounded-xl p-4 sm:p-6 mb-6 shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
            <h3 className="font-semibold flex items-center text-gray-800">
              <Bed className="w-5 h-5 mr-2 text-blue-600" />
              Room Management
            </h3>
            
            {!showPricingPanel && (
              <button 
                onClick={() => setShowPricingPanel(true)}
                className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors flex items-center font-medium"
              >
                <Coins className="w-4 h-4 mr-1" />
                Show Pricing Panel
              </button>
            )}
          </div>
          
          {/* Search and Filter Bar */}
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-grow max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search rooms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:outline-none"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            
            <div className="flex-grow flex items-center justify-end">
              <button
                onClick={() => setShowRoomTips(!showRoomTips)}
                className={`px-3 py-1.5 rounded-lg text-sm flex items-center mr-2
                  ${showRoomTips 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                <HelpCircle className="w-4 h-4 mr-1" />
                Tips
              </button>
              
            </div>
          </div>
          
          {/* Filter chips for room filtering */}
          <div className="flex flex-wrap gap-2 mb-4">
            {renderFilterBadge('all', 'All Rooms', Bed)}
            {renderFilterBadge('occupied', 'Occupied', Users)}
            {renderFilterBadge('vacant', 'Vacant', CheckCircle)}
            
            {/* Room type filters */}
            {Object.keys(roomsByType).map(type => {
              const details = getRoomTypeDetails(type);
              return renderFilterBadge(type, type, () => details.icon);
            })}
          </div>
          
          {/* Room management tips */}
          {showRoomTips && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex justify-between items-start">
                <h4 className="font-medium text-blue-700 mb-2 flex items-center">
                  <HelpCircle className="w-4 h-4 mr-1" />
                  Quick Room Management Tips
                </h4>
                <button 
                  onClick={() => setShowRoomTips(false)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <ul className="text-sm text-blue-800 space-y-1">
                <li className="flex items-start">
                  <Star className="w-3 h-3 mr-1 mt-1 flex-shrink-0" />
                  <span>Higher quality rooms can be priced higher and attract more guests</span>
                </li>
                <li className="flex items-start">
                  <DollarSign className="w-3 h-3 mr-1 mt-1 flex-shrink-0" />
                  <span>Use the bulk price adjustment feature during peak seasons</span>
                </li>
                <li className="flex items-start">
                  <Coins className="w-3 h-3 mr-1 mt-1 flex-shrink-0" />
                  <span>Balance your room type distribution based on your target market</span>
                </li>
              </ul>
            </div>
          )}
          
          {/* Enhanced Room Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th 
                    className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center">
                      Room Details
                      {sortBy === 'id' && (
                        <ArrowUpDown className="w-3 h-3 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('quality')}
                  >
                    <div className="flex items-center">
                      Quality
                      {sortBy === 'quality' && (
                        <ArrowUpDown className="w-3 h-3 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('price')}
                  >
                    <div className="flex items-center">
                      Pricing
                      {sortBy === 'price' && (
                        <ArrowUpDown className="w-3 h-3 ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {sortBy === 'status' && (
                        <ArrowUpDown className="w-3 h-3 ml-1" />
                      )}
                    </div>
                  </th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedRooms.length > 0 ? (
                  sortedRooms.map((room) => {
                    const details = getRoomTypeDetails(room.type);
                    const currentUpgradeCost = calculateUpgradeCost(room.quality);
                    const isBelowMaxQuality = room.quality < MAX_QUALITY;
                    const canAfford = money >= currentUpgradeCost;
                    const canUpgrade = isBelowMaxQuality && canAfford;
                    
                    return (
                      <tr key={room.id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-3">
                          <div className="flex items-center">
                            <div 
                              className={`flex-shrink-0 h-10 w-10 rounded-lg bg-${details.color}-100 flex items-center justify-center`}
                            >
                              {details.icon}
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">Room {room.id}</div>
                              <div className="text-sm text-gray-500 flex items-center">
                                {room.type}
                                <span className="mx-1.5 h-1 w-1 rounded-full bg-gray-300"></span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center">
                            <div className="flex">{renderStars(room.quality)}</div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <div className="font-medium text-gray-900 flex items-center mr-3">
                                <DollarSign className="w-4 h-4 text-green-500 mr-1" />
                                {formatMoney(room.price)}
                              </div>
                              <div className="flex">
                                <button
                                  onClick={() => adjustRoomPrice(room.id, Math.max(10, room.price - 10))}
                                  className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors rounded-l-md border border-gray-300"
                                  title="Decrease price by $10"
                                >
                                  <ArrowDown className="w-4 h-4 text-gray-600" />
                                </button>
                                <button
                                  onClick={() => adjustRoomPrice(room.id, room.price + 10)}
                                  className="p-1 bg-gray-100 hover:bg-gray-200 transition-colors rounded-r-md border-t border-r border-b border-gray-300"
                                  title="Increase price by $10"
                                >
                                  <ArrowUp className="w-4 h-4 text-gray-600" />
                                </button>
                              </div>
                            </div>
                            <div className="text-xs">
                              {room.price > 100 ? (
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-md">Premium</span>
                              ) : room.price > 70 ? (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-md">Standard</span>
                              ) : (
                                <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-md">Budget</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="space-y-2">
                            <div className={`flex items-center ${room.occupied ? 'text-red-600' : 'text-green-600'}`}>
                              {room.occupied ? (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md flex items-center text-xs font-medium">
                                  <Users className="w-3 h-3 mr-1" />
                                  Occupied
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md flex items-center text-xs font-medium">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Vacant
                                </span>
                              )}
                            </div>
                            
                            {room.occupied && room.guestCheckoutDay && (
                              <div className="text-xs text-gray-500 flex items-center bg-gray-50 px-2 py-1 rounded-md">
                                <Calendar className="w-3 h-3 mr-1" />
                                Checkout: Day {room.guestCheckoutDay}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col space-y-2">
                          {isBelowMaxQuality ? (
              <button
                onClick={() => upgradeRoom(room.id)} // Assuming upgradeRoom is available
                disabled={!canAfford} // Disable only if cannot afford (already checked quality)
                title={canAfford ? `Upgrade Cost: ${formatCurrency(currentUpgradeCost)}` : `Need ${formatCurrency(currentUpgradeCost)}`}
              >
                Upgrade {canAfford ? `($${formatCurrency(currentUpgradeCost)})` : `($${formatCurrency(currentUpgradeCost)})`}
              </button>
            ) : (
              <span className="max-quality-text">Max Quality</span>
            )}
            {!canAfford && isBelowMaxQuality && (
                 <p className="cannot-afford-text">(Need ${formatCurrency(currentUpgradeCost - money)} more)</p>
            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="p-6 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <Bed className="w-10 h-10 text-gray-300 mb-2" />
                        <p>No rooms match your current filters</p>
                        {(activeFilter !== 'all' || searchTerm) && (
                          <button 
                            onClick={() => {
                              setActiveFilter('all');
                              setSearchTerm('');
                            }}
                            className="mt-2 text-blue-600 hover:text-blue-800"
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Room table pagination/summary */}
          <div className="mt-3 text-sm text-gray-500 flex justify-between items-center">
            <div>
              Showing {sortedRooms.length} of {rooms.length} rooms
            </div>
            <div className="flex items-center">
              <span className="mr-2">Manage Page Size:</span>
              <select className="border rounded-md p-1 text-sm">
                <option>10 per page</option>
                <option>25 per page</option>
                <option>50 per page</option>
                <option>All</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tips Section with improved design */}
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 p-4 rounded-xl border border-blue-100 shadow-sm">
          <h3 className="font-bold mb-4 flex items-center text-blue-800">
            <Star className="w-5 h-5 mr-2" />
            Room Management Strategies
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white bg-opacity-60 p-4 rounded-lg border border-blue-100">
              <h4 className="font-medium mb-3 text-blue-700 flex items-center">
                <DollarSign className="w-4 h-4 mr-1" />
                Quality & Pricing Strategies
              </h4>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    <Star className="w-3 h-3 text-blue-600" />
                  </div>
                  <span>Higher quality rooms allow you to charge premium prices and improve guest satisfaction ratings</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    <DollarSign className="w-3 h-3 text-blue-600" />
                  </div>
                  <span>Use the bulk price adjustment feature to quickly respond to market changes or seasonal demand</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    <BarChart className="w-3 h-3 text-blue-600" />
                  </div>
                  <span>Monitor competitor prices regularly and adjust your pricing strategy to maintain competitive edge</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white bg-opacity-60 p-4 rounded-lg border border-blue-100">
              <h4 className="font-medium mb-3 text-blue-700 flex items-center">
                <Home className="w-4 h-4 mr-1" />
                Room Strategy Optimization
              </h4>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    <Home className="w-3 h-3 text-blue-600" />
                  </div>
                  <span>Different room types attract different guest segments - balance your inventory based on your target market</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    <Users className="w-3 h-3 text-blue-600" />
                  </div>
                  <span>Invest in housekeeping staff to maintain room quality and prevent quality degradation over time</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    <AlertTriangle className="w-3 h-3 text-blue-600" />
                  </div>
                  <span>Regular maintenance prevents costly repairs and keeps room quality high, leading to better reviews</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default RoomManagementTab;